import axios from 'axios';
import { useQueryParamHelper } from '@shared/core/queryString';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { LinkV2, TextV2 } from '@withjoy/joykit';
import { StyledStatusBanner } from './StatusBanner.styles';
import { useHistory, useLocation } from '@react-router';
import { useFeatureValue } from '@shared/core/featureFlags';
import { useEventInfo } from '@shared/utils/eventInfo';
import { getConfig } from '@config';
import { SUCCESS_HOTEL_RECOMMENDATION_BANNER_KEY } from '@shared/constants/bannerKeys';
import { StatusBannerTelemetryProvider, useStatusBannerTelemetry } from './StatusBannerTelemetry';
import { addError } from '@shared/utils/logger';

const isDev = getConfig().environment === 'dev';

type BannerPayload = {
  bannerKey: string;
  bannerText: string;
  closeBannerCTA?: string;
  yesBannerCTA?: string;
  noBannerCTA?: string;
}[];

const isValidBanner = (payload: unknown): payload is BannerPayload[number] => {
  if (!payload) {
    return false;
  }

  if (!Object.hasOwn(payload, 'bannerKey')) {
    return false;
  }

  if (!Object.hasOwn(payload, 'bannerText')) {
    return false;
  }

  return true;
};

const isValidBannerData = (payload: unknown): payload is BannerPayload => {
  if (!payload) {
    return false;
  }

  if (!Array.isArray(payload)) {
    return false;
  }

  // If any objects don't have required fields, we are invalid.
  if ((payload as BannerPayload).some(banner => !isValidBanner(banner))) {
    return false;
  }

  return true;
};

export const useGetStatusBannerData = () => {
  const params = useQueryParamHelper();
  const bannerKey = params.getValue('bannerKey');
  const { value: bannerEnabled, payload: bannerPayload } = useFeatureValue('websiteBannerMapping', { skip: !bannerKey });

  if (bannerEnabled !== 'on' || !isValidBannerData(bannerPayload)) {
    return null;
  }

  return bannerPayload.find(banner => banner.bannerKey === bannerKey) ?? null;
};

const useSetBannerState = () => {
  const location = useLocation();
  const history = useHistory();

  const setBannerState = useCallback(
    (newState: string | undefined) => {
      const query = new URLSearchParams(location.search);
      if (newState && newState !== '') {
        query.set('bannerKey', newState);
      } else {
        query.delete('bannerKey');
      }
      const newQuery = query.toString();
      history.replace({ search: newQuery });
    },
    [history, location],
  );

  return { setBannerState };
};

export const StatusBanner = () => {
  const params = useQueryParamHelper();
  const guestId = params.getValue('guestId');
  const [isUpdateInviteePropertyLoading, setIsUpdateInviteePropertyLoading] = useState<boolean>(false);
  const bannerData = useGetStatusBannerData();
  const [isOpen, setIsOpen] = useState(!!bannerData);
  const hasCheckedData = useRef(false);
  const { setBannerState } = useSetBannerState();
  const { eventInfo } = useEventInfo();
  const { trackError, answerClicked, pageViewed } = useStatusBannerTelemetry();

  const updateInviteeProperty = useCallback(
    async (value: string) => {
      setIsUpdateInviteePropertyLoading(true);
      try {
        await axios.put(
          `/services/api/events/${eventInfo?.eventFirebaseId}/invitees/${guestId}/profile/needHotelRecommendations`,
          {
            propertyValue: value,
          },
          {
            baseURL: isDev ? 'https://dev.heygirl.cutestperson.com' : 'https://heygirl.cutestperson.com',
          },
        );
      } catch (e) {
        trackError(e);
      } finally {
        setIsUpdateInviteePropertyLoading(false);
      }
    },
    [eventInfo?.eventFirebaseId, guestId, trackError],
  );

  useEffect(() => {
    if (bannerData && !hasCheckedData.current) {
      setIsOpen(true);
      hasCheckedData.current = true;
      if (bannerData.bannerKey === SUCCESS_HOTEL_RECOMMENDATION_BANNER_KEY) {
        pageViewed();
      }
    }
  }, [bannerData, pageViewed]);

  const closeBanner = () => {
    setIsOpen(false);
    setBannerState('');
  };

  const answerAs = async (answerValue: string) => {
    try {
      setIsOpen(false);
      setBannerState('');
      await updateInviteeProperty(answerValue);
      answerClicked(answerValue);
    } catch (error) {
      addError(error, { context: 'StatusBanner', operation: 'answerAs' });
    }
  };

  if (!isOpen || !bannerData || isUpdateInviteePropertyLoading || (bannerData.bannerKey === SUCCESS_HOTEL_RECOMMENDATION_BANNER_KEY && (!eventInfo?.eventFirebaseId || !guestId))) {
    return null;
  }

  return (
    <StatusBannerTelemetryProvider>
      <StyledStatusBanner playConfetti={true}>
        <TextV2 typographyVariant="button1">{bannerData.bannerText}</TextV2>
        {!!bannerData.closeBannerCTA && (
          <LinkV2
            color="black"
            onClick={closeBanner}
            typographyVariant="body1"
          >
            {bannerData.closeBannerCTA}
          </LinkV2>
        )}
        {!!bannerData.yesBannerCTA && (
          <LinkV2
            color="black"
            onClick={() => answerAs('Yes')}
            typographyVariant="body1"
          >
            {bannerData.yesBannerCTA}
          </LinkV2>
        )}
        {!!bannerData.noBannerCTA && (
          <LinkV2
            color="black"
            onClick={() => answerAs('No')}
            typographyVariant="body1"
          >
            {bannerData.noBannerCTA}
          </LinkV2>
        )}
      </StyledStatusBanner>
    </StatusBannerTelemetryProvider>
  );
};
